import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, MessageCircle, ChevronDown } from 'lucide-react';

const faqs = [
  {
    q: 'How do I place an order?',
    a: 'Browse our collections, add items to cart, and click "Continue to WhatsApp Order" to send your inquiry via WhatsApp. Our team will assist you with the rest.'
  },
  {
    q: 'Do you deliver outside Karnataka?',
    a: 'Yes! We deliver pan-India. Delivery charges vary based on location. Contact us for specific details.'
  },
  {
    q: 'What is your return policy?',
    a: 'We accept returns within 7 days of delivery for manufacturing defects. Custom or personalized orders are non-returnable.'
  },
  {
    q: 'Can I visit the store in person?',
    a: 'Absolutely! We welcome walk-ins at our Gokul Road, Hubli location. We recommend booking an appointment via WhatsApp for bridal consultations.'
  },
  {
    q: 'Do you offer video consultations?',
    a: 'Yes! We offer live WhatsApp and Zoom video consultations for remote customers, NRIs, and out-of-city shoppers. Contact us to schedule.'
  }
];

const AccordionItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid rgba(229,169,60,0.2)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.25rem 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          gap: '1rem'
        }}
      >
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem', fontWeight: 600, color: '#2A0000' }}>{q}</span>
        <ChevronDown
          size={18}
          style={{
            color: '#E5A93C',
            flexShrink: 0,
            transition: 'transform 0.3s ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        />
      </button>
      <div style={{ maxHeight: open ? '200px' : '0', overflow: 'hidden', transition: 'max-height 0.35s ease' }}>
        <p style={{ color: '#5a3a2a', lineHeight: 1.75, paddingBottom: '1.25rem', fontSize: '0.95rem', margin: 0 }}>{a}</p>
      </div>
    </div>
  );
};

const ContactPage = () => {
  return (
    <div style={{ minHeight: '100vh', background: '#FFFDF9', fontFamily: "'Lato', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Lato:wght@300;400;700&display=swap');
        .whatsapp-btn:hover { background: #20BA5A !important; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(37,211,102,0.3) !important; }
        .call-btn:hover { background: rgba(42,0,0,0.06) !important; transform: translateY(-2px); }
        .info-card:hover .info-icon { transform: scale(1.12); background: rgba(229,169,60,0.2) !important; }
        .info-card { transition: all 0.2s; }
      `}</style>

      {/* ── Hero ── */}
      <section style={{
        background: 'linear-gradient(135deg, #1a0000 0%, #2A0000 60%, #3d0a00 100%)',
        color: 'white',
        padding: '5.5rem 1.5rem 4.5rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', borderRadius: '50%', border: '1px solid rgba(229,169,60,0.12)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '180px', height: '180px', borderRadius: '50%', border: '1px solid rgba(229,169,60,0.08)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-100px', left: '-100px', width: '340px', height: '340px', borderRadius: '50%', border: '1px solid rgba(229,169,60,0.1)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '30px', left: '10%', width: '80px', height: '80px', borderRadius: '50%', border: '1px solid rgba(229,169,60,0.1)', pointerEvents: 'none' }} />

        <p style={{ color: '#E5A93C', fontFamily: "'Cormorant Garamond', serif", fontSize: '1.4rem', opacity: 0.8, marginBottom: '0.5rem' }}>✦ ✦ ✦</p>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.8rem, 6vw, 4.5rem)', fontWeight: 700, margin: '0 0 1rem', letterSpacing: '0.03em', lineHeight: 1.1 }}>
          Get in Touch
        </h1>
        <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.65)', fontWeight: 300, letterSpacing: '0.18em', textTransform: 'uppercase', margin: '0 0 1.75rem' }}>
          We're here to help with your ethnic wear needs
        </p>
        <div style={{ width: '60px', height: '2px', background: '#E5A93C', margin: '0 auto' }} />
      </section>

      {/* ── Contact Info Cards ── */}
      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '0 1.5rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.25rem',
          marginTop: '-2rem',
          position: 'relative',
          zIndex: 10
        }}>
          {[
            { icon: <MapPin size={22} />, title: 'Visit Us', lines: ['Gokul Road, Hubli', 'Karnataka, India'], href: null },
            { icon: <Phone size={22} />, title: 'Call Us', lines: ['+91 7760591999'], href: 'tel:+917760591999' },
            { icon: <Mail size={22} />, title: 'Email Us', lines: ['ahmedabad44@gmail.com'], href: 'mailto:ahmedabad44@gmail.com' },
            { icon: <Clock size={22} />, title: 'Hours', lines: ['Monday–Sunday: 10:30 AM – 9 PM'], href: null },
          ].map(({ icon, title, lines, href }) => (
            <div
              key={title}
              className="info-card"
              style={{
                background: 'white',
                border: '1px solid rgba(229,169,60,0.2)',
                borderTop: '3px solid #E5A93C',
                borderRadius: '3px',
                padding: '1.5rem 1.25rem',
                textAlign: 'center',
                boxShadow: '0 4px 24px rgba(42,0,0,0.07)'
              }}
            >
              <div
                className="info-icon"
                style={{
                  width: '48px', height: '48px',
                  background: 'rgba(229,169,60,0.1)',
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#E5A93C',
                  margin: '0 auto 1rem',
                  transition: 'all 0.2s'
                }}
              >
                {icon}
              </div>
              <p style={{ fontSize: '0.7rem', fontWeight: 700, color: '#E5A93C', letterSpacing: '0.18em', textTransform: 'uppercase', margin: '0 0 0.5rem' }}>{title}</p>
              {lines.map((line, i) =>
                href && i === 0
                  ? <a key={i} href={href} style={{ display: 'block', color: '#2A0000', textDecoration: 'none', fontSize: '0.88rem', lineHeight: 1.6, fontWeight: i === 0 ? 700 : 400 }}>{line}</a>
                  : <p key={i} style={{ margin: 0, color: i === 0 ? '#2A0000' : '#7a5a4a', fontSize: '0.88rem', lineHeight: 1.6, fontWeight: i === 0 ? 700 : 400 }}>{line}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Map + CTA ── */}
      <div style={{ maxWidth: '960px', margin: '3rem auto 0', padding: '0 1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', alignItems: 'start' }}>

          {/* Map */}
          <div>
            <p style={{ color: '#E5A93C', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, marginBottom: '0.75rem' }}>Find Us</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.8rem', color: '#2A0000', margin: '0 0 1rem', fontWeight: 600 }}>Our Store Location</h2>
            <div style={{ borderRadius: '4px', overflow: 'hidden', border: '1px solid rgba(229,169,60,0.25)', height: '360px' }}>
              <iframe
                title="Store Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3847.4756539804484!2d75.10754601095981!3d15.350704885167856!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bb8d720db623ad5%3A0x30d3a953029b065a!2sAhmedabad%20Nx!5e0!3m2!1sen!2sus!4v1779722627150!5m2!1sen!2sus"
                width="100%"
                height="360"
                style={{ border: 0, display: 'block' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Reach out panel */}
          <div>
            <p style={{ color: '#E5A93C', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, marginBottom: '0.75rem' }}>Connect</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.8rem', color: '#2A0000', margin: '0 0 0.75rem', fontWeight: 600 }}>Reach Out to Us</h2>
            <p style={{ color: '#5a3a2a', fontSize: '0.95rem', lineHeight: 1.75, marginBottom: '2rem' }}>
              Whether you're looking for the perfect saree, planning a bridal trousseau, or need styling advice — our team is just a message away. We'd love to assist you personally.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <a
                href="https://wa.me/917760591999"
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-btn"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
                  background: '#25D366', color: 'white',
                  padding: '1rem 1.5rem',
                  borderRadius: '3px',
                  textDecoration: 'none',
                  fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.1em',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 12px rgba(37,211,102,0.2)'
                }}
              >
                <MessageCircle size={18} /> Chat on WhatsApp
              </a>
              <a
                href="tel:+919538769669"
                className="call-btn"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
                  background: 'transparent', color: '#2A0000',
                  padding: '1rem 1.5rem',
                  borderRadius: '3px',
                  textDecoration: 'none',
                  fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.1em',
                  border: '1.5px solid #2A0000',
                  transition: 'all 0.2s'
                }}
              >
                <Phone size={18} /> Call Us Now
              </a>
            </div>

            {/* Decorative note */}
            <div style={{ marginTop: '2rem', padding: '1.25rem', background: 'rgba(229,169,60,0.07)', borderLeft: '3px solid #E5A93C', borderRadius: '0 3px 3px 0' }}>
              <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '1rem', color: '#2A0000', lineHeight: 1.6 }}>
                "For bridal consultations, we recommend booking an appointment in advance for a personalized experience."
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <section style={{ background: 'linear-gradient(to bottom, #fff9f0, #FFFDF9)', padding: '5rem 1.5rem', marginTop: '4rem' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <p style={{ color: '#E5A93C', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, marginBottom: '0.5rem' }}>Got Questions?</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', color: '#2A0000', margin: '0 0 1rem', fontWeight: 600 }}>Common Questions</h2>
            <div style={{ width: '40px', height: '2px', background: '#E5A93C', margin: '0 auto' }} />
          </div>
          <div style={{ background: 'white', borderRadius: '4px', border: '1px solid rgba(229,169,60,0.2)', padding: '0 1.75rem' }}>
            {faqs.map((faq) => <AccordionItem key={faq.q} q={faq.q} a={faq.a} />)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;